import { useEffect, useState } from 'react';
import { Minus, Square, X, Maximize2, RotateCcw } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import MapleOSLogo from '@/components/common/MapleOSLogo';
import { useToast } from '@/components/ui/use-toast';
import { resetBrokenWindowsState } from '@/OS/lib/appRecovery';

const getThemeRoot = () =>
  typeof document === 'undefined'
    ? null
    : ((document.querySelector('.mapleos-root') as HTMLElement | null) ?? document.documentElement);

const getHslVariable = (root: HTMLElement, variableName: string, fallback: string) => {
  const value = window.getComputedStyle(root).getPropertyValue(variableName).trim();
  return value ? `hsl(${value})` : fallback;
};

/**
 * Custom title bar for Electron with Discord-style window controls
 * Features:
 * - Dark/light mode support
 * - Lime-accented window control buttons
 * - Draggable area
 * - Double-click to maximize/restore
 * - F11 keyboard shortcut for fullscreen toggle
 */
export function TitleBar() {
  const [isMaximized, setIsMaximized] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isDark, setIsDark] = useState(true);
  const [titleBarBackground, setTitleBarBackground] = useState('hsl(220 20% 8%)');
  const [titleBarBorder, setTitleBarBorder] = useState('hsl(220 20% 15%)');
  const location = useLocation();
  const { toast } = useToast();
  const isElectron = typeof window !== 'undefined' && window.electronAPI?.isElectron;
  const showResetBrokenWindows = isElectron;
  const isAuthLikeRoute = ['/auth', '/start'].some((route) => location.pathname.startsWith(route));

  useEffect(() => {
    console.log('TitleBar: window.electronAPI =', window.electronAPI);
    console.log('TitleBar: isElectron =', isElectron);
  }, []);

  useEffect(() => {
    if (isElectron) {
      window.electronAPI.windowIsMaximized().then(setIsMaximized);
      window.electronAPI.windowIsFullScreen().then((fullscreen) => {
        setIsFullScreen(fullscreen);
        if (fullscreen) {
          document.body.classList.add('electron-fullscreen');
        }
      });
      document.body.classList.add('electron-app');

      const interval = setInterval(() => {
        window.electronAPI.windowIsFullScreen().then((fullscreen) => {
          setIsFullScreen(fullscreen);
          if (fullscreen) {
            document.body.classList.add('electron-fullscreen');
          } else {
            document.body.classList.remove('electron-fullscreen');
          }
        });
      }, 500);

      return () => {
        document.body.classList.remove('electron-app');
        document.body.classList.remove('electron-fullscreen');
        clearInterval(interval);
      };
    }
  }, [isElectron]);

  useEffect(() => {
    const updateTheme = () => {
      const root = getThemeRoot();
      if (!root) return;

      const darkMode = root.classList.contains('dark');
      setIsDark(darkMode);
      setTitleBarBackground(
        getHslVariable(root, '--background', darkMode ? 'hsl(220 20% 8%)' : 'hsl(200 15% 95%)')
      );
      setTitleBarBorder(
        getHslVariable(root, '--border', darkMode ? 'hsl(220 20% 15%)' : 'hsl(200 10% 82%)')
      );
    };

    updateTheme();

    const observer = new MutationObserver(updateTheme);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    return () => observer.disconnect();
  }, []);

  const handleMinimize = () => {
    if (isElectron) {
      window.electronAPI.windowMinimize();
    }
  };

  const handleMaximize = () => {
    if (isElectron) {
      window.electronAPI.windowMaximize();
      setIsMaximized(!isMaximized);
    }
  };

  const handleClose = () => {
    if (isElectron) {
      window.electronAPI.windowClose();
    }
  };

  const handleFullscreen = () => {
    if (isElectron) {
      window.electronAPI.windowFullscreen();
    }
  };

  const handleResetBrokenWindows = () => {
    const confirmed = window.confirm(
      'Reset broken windows? This will close all MapleOS windows, clear saved window restore state, and reload the desktop.'
    );

    if (!confirmed) {
      return;
    }

    resetBrokenWindowsState();
    toast({
      title: 'Broken windows reset',
      description: 'Saved MapleOS window state was cleared. Reloading now…',
    });
    window.setTimeout(() => {
      window.location.reload();
    }, 150);
  };

  const handleDoubleClick = () => {
    handleMaximize();
  };

  if (!isElectron) {
    console.log('TitleBar: Not rendering - isElectron is false');
    return null;
  }

  if (isFullScreen) {
    console.log('TitleBar: Not rendering - in fullscreen mode');
    return null;
  }

  console.log('TitleBar: Rendering title bar');

  return (
    <div
      className="fixed top-0 left-0 right-0 z-50 flex h-8 select-none items-center justify-between border-b"
      style={{
        WebkitAppRegion: 'drag',
        backgroundColor: titleBarBackground,
        borderColor: titleBarBorder,
      } as any}
      onDoubleClick={handleDoubleClick}
    >
      <div className="flex flex-1 items-center gap-3 px-4">
        <MapleOSLogo
          className="h-[18px] w-auto"
          iconClassName="text-[hsl(75_100%_42%)]"
          textClassName={isDark ? 'text-gray-200' : 'text-slate-800'}
        />
      </div>

      <div className="flex h-full items-center gap-1 pr-1" style={{ WebkitAppRegion: 'no-drag' } as any}>
        {showResetBrokenWindows && (
          <button
            onClick={handleResetBrokenWindows}
            className={cn(
              'group flex h-full items-center justify-center px-4 transition-colors',
              'hover:bg-[hsl(75_100%_42%_/_0.14)]'
            )}
            aria-label="Reset broken windows"
            title={
              isAuthLikeRoute
                ? 'Clear saved MapleOS window state before entering the desktop'
                : 'Clear saved MapleOS window state and reload'
            }
          >
            <RotateCcw
              className={cn(
                'h-3.5 w-3.5 transition-colors',
                isDark ? 'text-gray-400' : 'text-slate-700',
                'group-hover:text-[hsl(75_100%_36%)]'
              )}
              strokeWidth={2.5}
            />
          </button>
        )}

        <button
          onClick={handleFullscreen}
          className={cn(
            'group flex h-full items-center justify-center px-4 transition-colors',
            'hover:bg-[hsl(75_100%_42%_/_0.14)]'
          )}
          aria-label="Fullscreen (F11)"
          title="Fullscreen (F11 to exit)"
        >
          <Maximize2
            className={cn(
              'h-3.5 w-3.5 transition-colors',
              isDark ? 'text-gray-400' : 'text-slate-700',
              'group-hover:text-[hsl(75_100%_36%)]'
            )}
            strokeWidth={2.5}
          />
        </button>

        <button
          onClick={handleMinimize}
          className={cn(
            'group flex h-full items-center justify-center px-4 transition-colors',
            'hover:bg-[hsl(75_100%_42%_/_0.14)]'
          )}
          aria-label="Minimize"
        >
          <Minus
            className={cn(
              'h-4 w-4 transition-colors',
              isDark ? 'text-gray-400' : 'text-slate-700',
              'group-hover:text-[hsl(75_100%_36%)]'
            )}
            strokeWidth={2.5}
          />
        </button>

        <button
          onClick={handleMaximize}
          className={cn(
            'group flex h-full items-center justify-center px-4 transition-colors',
            'hover:bg-[hsl(75_100%_42%_/_0.14)]'
          )}
          aria-label={isMaximized ? 'Restore' : 'Maximize'}
        >
          <Square
            className={cn(
              'h-3.5 w-3.5 transition-colors',
              isDark ? 'text-gray-400' : 'text-slate-700',
              'group-hover:text-[hsl(75_100%_36%)]'
            )}
            strokeWidth={2.5}
          />
        </button>

        <button
          onClick={handleClose}
          className={cn(
            'group flex h-full items-center justify-center px-4 transition-colors',
            'hover:bg-[hsl(75_100%_42%)]'
          )}
          aria-label="Close"
        >
          <X
            className={cn(
              'h-4 w-4 transition-colors',
              isDark ? 'text-gray-400' : 'text-slate-700',
              'group-hover:text-black'
            )}
            strokeWidth={2.5}
          />
        </button>
      </div>
    </div>
  );
}